import { Component } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * 한 화면의 오류가 서비스 전체를 빈 화면으로 만들지 않게 막는다.
 *
 * 리액트는 그리는 도중 예외가 나면 화면을 통째로 비운다. 실제로 공고 하나의
 * 첨부파일 자리에 null이 들어 있어서, 그 공고 상세가 백지로 보인 일이 있었다.
 * 서버가 보내는 값은 우리가 정할 수 없으므로, 터질 수 있다는 전제로 둔다.
 *
 * 여기서 하는 일은 세 가지다.
 *   - 사용자는 백지 대신 무슨 일이 났는지 보고 되돌아갈 수 있다
 *   - 오류를 콘솔에 남겨 어느 화면인지 알 수 있다
 *   - 경로가 바뀌면 스스로 복구한다 (resetKey)
 */
export default class ErrorBoundary extends Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[화면 오류]', this.props.name ?? '', error, info?.componentStack);
  }

  componentDidUpdate(prev) {
    // 다른 화면으로 옮겨가면 오류 상태를 들고 가지 않는다
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    return (
      <div role="alert" style={{
        maxWidth: 480, margin: '80px auto', padding: '0 24px', textAlign: 'center',
        fontFamily: 'Pretendard, sans-serif', color: '#2B2B2B',
      }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 10px' }}>
          화면을 여는 중 문제가 생겼습니다
        </h1>
        <p style={{ fontSize: 15, lineHeight: 1.7, color: '#6B6B75', margin: '0 0 24px' }}>
          일시적인 문제일 수 있습니다. 다시 시도해도 같다면 알려주세요.
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 18px', borderRadius: 10, border: 'none',
              background: '#2B2B2B', color: '#fff', fontSize: 14, cursor: 'pointer',
            }}
          >
            다시 시도
          </button>
          <button
            onClick={() => { window.location.href = '/'; }}
            style={{
              padding: '10px 18px', borderRadius: 10, border: '1px solid #E0E0E5',
              background: '#fff', color: '#2B2B2B', fontSize: 14, cursor: 'pointer',
            }}
          >
            홈으로
          </button>
        </div>
      </div>
    );
  }
}

/**
 * 라우터 안에서 쓰는 안전망. 경로가 바뀌면 스스로 풀린다 —
 * 한 화면에서 터졌다고 다른 화면까지 못 보면 안 된다.
 */
export function RouteErrorBoundary({ children }) {
  const { pathname } = useLocation();
  return (
    <ErrorBoundary resetKey={pathname} name={pathname}>
      {children}
    </ErrorBoundary>
  );
}
